import instance from '@/config/axios'
import type {
  ChangePasswordRequest,
  ChangePasswordResponse,
  CheckVerificationRequest,
  CheckVerificationResponse,
  GoogleSignupRequest,
  PostOauthRequest,
  PostOauthResponse,
  PostVerificationRequest,
  PostVerificationResponse,
  SigninRequest,
  SignupResponse,
  WithdrawResponse,
} from './type'
import { useCollableMutation } from '@/config/useCollableMutation'
import { authApi, useToastPopup } from '@collable/ui'
import { useQueryClient } from '@tanstack/react-query'

export const apiSignin = async (payload: SigninRequest) => {
  return authApi.signIn<SigninRequest, { token: string }>(instance, payload)
}

export const useSignin = () => {
  const queryClient = useQueryClient()
  return useCollableMutation({
    mutationFn: apiSignin,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/v1/me'] })
    },
  })
}

export const apiPostVerification = async (payload: PostVerificationRequest) => {
  return authApi.postVerification<PostVerificationRequest, PostVerificationResponse>(
    instance,
    payload,
  )
}

export const usePostVerification = () => {
  return useCollableMutation({
    mutationFn: apiPostVerification,
  })
}

export const apiCheckVerification = async (payload: CheckVerificationRequest) => {
  return authApi.checkVerification<CheckVerificationRequest, CheckVerificationResponse>(
    instance,
    payload,
  )
}

export const useCheckVerification = () => {
  return useCollableMutation({
    mutationFn: apiCheckVerification,
  })
}

/**
 * 이메일 회원가입인경우 email과 password 필요
 * 구글 회원가입인경우 token 필요
 */
export const apiSignup = async (payload: Omit<GoogleSignupRequest, 'userType'>) => {
  return authApi.signUp<typeof payload & { userType: 'CREATOR' }, SignupResponse>(instance, {
    userType: 'CREATOR',
    ...payload,
  })
}

export const useSignup = () => {
  const queryClient = useQueryClient()
  return useCollableMutation({
    mutationFn: apiSignup,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/v1/me'] })
    },
  })
}

export const apiChangePassword = async (payload: ChangePasswordRequest) => {
  return authApi.changePassword<ChangePasswordRequest, ChangePasswordResponse>(instance, payload)
}

export const useChangePassword = () => {
  const { addToastPopup } = useToastPopup()
  return useCollableMutation({
    mutationFn: apiChangePassword,
    onSuccess: () => {
      addToastPopup({
        type: 'normal',
        message: '비밀번호가 변경되었습니다',
        status: 'positive',
      })
    },
  })
}

export const apiPostOauth2 = async (payload: PostOauthRequest) => {
  const res = await instance.post<PostOauthResponse>(`/v1/oauth2`, payload)
  return res.data
}

export const usePostOauth = () => {
  return useCollableMutation({
    mutationFn: (payload: PostOauthRequest) => apiPostOauth2({ ...payload, type: 'CREATOR' }),
  })
}

export const apiWithdraw = async () => {
  return authApi.withdraw<WithdrawResponse>(instance)
}

export const useWithdraw = () => {
  const { addToastPopup } = useToastPopup()
  return useCollableMutation({
    mutationFn: apiWithdraw,
    onSuccess: () => {
      addToastPopup({
        type: 'normal',
        message: '회원탈퇴가 완료되었습니다',
        status: 'positive',
      })
    },
  })
}
